import apiClient from './apiClient';
import { ApiResponse } from '../types';
import {
  BacktestResponse,
  LeadCapturePayload,
  LeadCaptureResponse,
  MarketplaceListing,
  MarketplaceListingDetail,
  MarketplaceSubscription,
  SubscribeRequest,
  PublishBasketRequest,
  UpdateSubscriptionRequest,
  UpdateListingRequest,
} from '../types/marketplace';

const API_TYPE = 'api' as const;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

class MarketplaceService {
  /**
   * List all published marketplace strategies (public)
   */
  async getListings(filters?: { category?: string; risk_level?: string }): Promise<MarketplaceListing[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.set('category', filters.category);
      if (filters?.risk_level) params.set('risk_level', filters.risk_level);

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await apiClient.get<ApiResponse<MarketplaceListing[]>>(
        `/marketplace/strategies${query}`, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch listings');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch marketplace strategies');
    }
  }

  /**
   * Get full detail for a marketplace strategy (public)
   */
  async getListing(basketId: string): Promise<MarketplaceListingDetail> {
    try {
      const response = await apiClient.get<ApiResponse<MarketplaceListingDetail>>(
        `/marketplace/strategies/${basketId}`, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy detail');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch strategy detail');
    }
  }

  /**
   * Subscribe to a marketplace strategy (protected)
   */
  async subscribe(request: SubscribeRequest): Promise<{ subscription_id: string; allocation_id: string }> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        '/marketplace/subscribe', request, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to subscribe');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to subscribe');
    }
  }

  /**
   * List user's subscriptions (protected)
   */
  async getSubscriptions(): Promise<MarketplaceSubscription[]> {
    try {
      const response = await apiClient.get<ApiResponse<MarketplaceSubscription[]>>(
        '/marketplace/subscriptions', undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch subscriptions');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch subscriptions');
    }
  }

  /**
   * Update a subscription (pause/resume/change lots)
   */
  async updateSubscription(subscriptionId: string, updates: UpdateSubscriptionRequest): Promise<void> {
    try {
      const response = await apiClient.put<ApiResponse<any>>(
        `/marketplace/subscriptions/${subscriptionId}`, updates, undefined, API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update subscription');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update subscription');
    }
  }

  /**
   * Cancel/unsubscribe from a marketplace strategy
   */
  async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        `/marketplace/subscriptions/${subscriptionId}`, undefined, API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to unsubscribe');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to unsubscribe');
    }
  }

  /**
   * List marketplace listings.
   *
   * scope='mine' (default) — caller's own listings; Admins only.
   *                          Used by Manage Listings (AdminMarketplacePage).
   * scope='all'            — every currently-published listing; Admins ∨
   *                          AccountManagers. Used by SubscribeOnBehalfDialog
   *                          so AMs can subscribe their managed users to
   *                          listings they didn't publish themselves.
   */
  async getAdminListings(scope: 'mine' | 'all' = 'mine'): Promise<MarketplaceListing[]> {
    const url = scope === 'all'
      ? '/admin/marketplace/listings?scope=all'
      : '/admin/marketplace/listings';
    try {
      const response = await apiClient.get<ApiResponse<MarketplaceListing[]>>(
        url, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch admin listings');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch admin listings');
    }
  }

  /**
   * Update a marketplace listing's metadata (admin only)
   */
  async updateListing(basketId: string, updates: UpdateListingRequest): Promise<MarketplaceListing> {
    try {
      const response = await apiClient.put<ApiResponse<MarketplaceListing>>(
        `/admin/marketplace/listing/${basketId}`, updates, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update listing');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update listing');
    }
  }

  /**
   * Publish a basket to marketplace (admin only)
   */
  async publishBasket(request: PublishBasketRequest): Promise<MarketplaceListing> {
    try {
      const response = await apiClient.post<ApiResponse<MarketplaceListing>>(
        '/admin/marketplace/publish', request, undefined, API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to publish basket');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to publish basket');
    }
  }

  /**
   * Fetch precomputed backtest stats for a listing (public). Returns null on 404.
   */
  async getBacktest(basketId: string): Promise<BacktestResponse | null> {
    try {
      const response = await apiClient.get<ApiResponse<BacktestResponse>>(
        `/marketplace/strategies/${basketId}/backtest`, undefined, API_TYPE
      );
      if (!response.success || !response.data) return null;
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch backtest');
    }
  }

  /**
   * Upload a backtest CSV for a listing (admin only). Expects a File with
   * a 'Date,Profit' CSV. Returns the parsed stats so the UI can render immediately.
   */
  async uploadBacktest(basketId: string, file: File): Promise<BacktestResponse> {
    const csv_base64 = await fileToBase64(file);
    try {
      const response = await apiClient.post<ApiResponse<BacktestResponse>>(
        `/admin/marketplace/listing/${basketId}/backtest`,
        { csv_base64 },
        undefined,
        API_TYPE
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to upload backtest');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to upload backtest');
    }
  }

  /**
   * Delete a listing's backtest (admin only).
   */
  async deleteBacktest(basketId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        `/admin/marketplace/listing/${basketId}/backtest`, undefined, API_TYPE
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete backtest');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to delete backtest');
    }
  }

  /**
   * Capture an anonymous lead (public, no auth). Used for the Zebu Chennai event walk-ins.
   */
  async captureLead(payload: LeadCapturePayload): Promise<LeadCaptureResponse> {
    try {
      const response = await apiClient.post<LeadCaptureResponse>(
        '/marketplace/leads', payload, undefined, API_TYPE
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to submit');
      }
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to submit');
    }
  }

  /**
   * Unpublish a basket from marketplace (admin only)
   */
  async unpublishBasket(basketId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        '/admin/marketplace/unpublish', { basket_id: basketId }, undefined, API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to unpublish basket');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to unpublish basket');
    }
  }

  /**
   * Get count of non-terminal positions across all subscribers of a listing.
   * Used by the Unpublish dialog to inform the wind-down decision.
   */
  async getListingOpenPositions(basketId: string): Promise<{
    basket_id: string;
    strategy_count: number;
    total: number;
    by_status: { open: number; entry_pending: number; exit_pending: number };
    affected_user_count: number;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `/admin/marketplace/listing/${encodeURIComponent(basketId)}/open-positions`,
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch open positions');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch open positions');
    }
  }

  /**
   * Bulk-pause every strategy in a basket — admin wind-down step before
   * unpublishing. Flips `status` to INACTIVE; existing positions continue to
   * exit naturally per the engine's design.
   */
  async pauseAllStrategiesInBasket(basketId: string): Promise<{
    updated_count: number;
    skipped_count: number;
    total: number;
  }> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `/baskets/${encodeURIComponent(basketId)}/strategies/actions`,
        { action: 'pause_all' },
        undefined,
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to pause strategies');
      }
      return {
        updated_count: (response as any).updated_count ?? 0,
        skipped_count: (response as any).skipped_count ?? 0,
        total: (response as any).total ?? 0,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to pause strategies');
    }
  }

  /**
   * Resume every strategy in a basket — reverses `pauseAllStrategiesInBasket`.
   */
  async resumeAllStrategiesInBasket(basketId: string): Promise<{
    updated_count: number;
    skipped_count: number;
    total: number;
  }> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `/baskets/${encodeURIComponent(basketId)}/strategies/actions`,
        { action: 'resume_all' },
        undefined,
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to resume strategies');
      }
      return {
        updated_count: (response as any).updated_count ?? 0,
        skipped_count: (response as any).skipped_count ?? 0,
        total: (response as any).total ?? 0,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to resume strategies');
    }
  }
}

const marketplaceService = new MarketplaceService();
export default marketplaceService;
export { MarketplaceService };
